import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import {
  DateOrder, ImportResult, ImportedLine, displayDate, parseDate, parseImportRows, templateColumns, textToTable,
} from '../../utils/prLineImport';

/**
 * Create PR › Import lines: download the Excel template, then upload a filled one or paste cells
 * copied from Excel. Every row is checked (required values, numbers, dates, item codes against
 * the synced SAP items) and shown before anything reaches the grid; rows with errors are skipped.
 */

export type ImportMode = 'append' | 'replace';

const PREVIEW_ROWS = 300;
const itemCodeOf = (item: any) => String(item?.data?.ItemCode ?? item?.ItemCode ?? '').trim();

export function downloadPRLineTemplate(requisitionType: string): void {
  const columns = templateColumns(requisitionType);
  const lines = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header)]);
  lines['!cols'] = columns.map((c) => ({ wch: Math.max(12, c.header.length + 4) }));
  const help = XLSX.utils.aoa_to_sheet([
    ['Column', 'What to enter'],
    ...columns.map((c) => [c.header, c.note]),
    [],
    ['Notes'],
    ['Keep the column names in the first row of the "PR Lines" sheet. Columns can be left out or put in another order.'],
    ['One row per PR line; empty rows are skipped. Rows with errors are shown and skipped when importing.'],
    ['Format the Item Code column as Text when codes start with 0.'],
  ]);
  help['!cols'] = [{ wch: 22 }, { wch: 100 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, lines, 'PR Lines');
  XLSX.utils.book_append_sheet(book, help, 'Instructions');
  XLSX.writeFile(book, `PR_Lines_Template${requisitionType ? `_${requisitionType}` : ''}.xlsx`);
}

const PRLineImportDialog: React.FC<{
  open: boolean;
  requisitionType: string;
  serviceUnitPriceMustBeOne: boolean;
  /** Lines already in the grid with an item code or description. */
  existingCount: number;
  onClose: () => void;
  onImport: (lines: ImportedLine[], mode: ImportMode, cachedItems: any[]) => void;
}> = ({ open, requisitionType, serviceUnitPriceMustBeOne, existingCount, onClose, onImport }) => {
  const [tab, setTab] = React.useState(0);
  const [pasted, setPasted] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [result, setResult] = React.useState<ImportResult | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [error, setError] = React.useState('');
  const [itemNote, setItemNote] = React.useState('');
  const [mode, setMode] = React.useState<ImportMode>('append');
  const [dateOrder, setDateOrder] = React.useState<DateOrder | undefined>(undefined);
  const items = React.useRef<any[] | null>(null);
  const lastTable = React.useRef<unknown[][] | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setTab(0); setPasted(''); setFileName(''); setResult(null); setError(''); setItemNote('');
    setMode(existingCount > 0 ? 'append' : 'replace');
    setDateOrder(undefined);
    items.current = null;
    lastTable.current = null;
  }, [open, existingCount]);

  /** Parse, then check item codes against the synced SAP items (also fills description and UoM). */
  const check = async (table: unknown[][], order: DateOrder | undefined = dateOrder) => {
    setError(''); setItemNote('');
    lastTable.current = table;
    const parsed = parseImportRows(table, { requisitionType, serviceUnitPriceMustBeOne, dateOrder: order });
    if (!parsed.rows.length) { setResult(null); setError('No lines found. Keep the column names in the first row (see the template).'); return; }
    setChecking(true);
    try {
      if (parsed.rows.some((r) => r.line.itemcode)) {
        if (!items.current) {
          const res: any = await apiService.listLocalItemMaster(undefined, undefined, requisitionType);
          items.current = (res?.data || res || []) as any[];
        }
        const byCode = new Map(items.current.map((it: any) => [itemCodeOf(it).toUpperCase(), it]));
        if (!byCode.size) {
          setItemNote('Item codes could not be checked here (no synced SAP items); they are checked when the PR is submitted.');
        } else {
          for (const row of parsed.rows) {
            const code = String(row.line.itemcode ?? '').trim();
            if (!code) continue;
            const item = byCode.get(code.toUpperCase());
            if (!item) { row.errors.push(`Item ${code} is not in the synced SAP items`); continue; }
            row.line.itemcode = itemCodeOf(item);
            if (!row.line.itemdescription) row.line.itemdescription = String(item.data?.ItemName ?? item.ItemName ?? '');
            if (!row.line.uomcode) {
              row.line.uomcode = String(item.data?.PurchaseUnit ?? item.PurchaseUnit ?? item.data?.InventoryUOM ?? item.InventoryUOM ?? '');
            }
          }
        }
      }
      setResult({ ...parsed });
    } catch (e: any) {
      setError(String(e?.response?.data?.message || e?.message || 'Could not check the item codes'));
      setResult({ ...parsed });
    } finally {
      setChecking(false);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    try {
      // raw: a CSV keeps its text as typed (SheetJS would read 05/06/2026 as a US date); real Excel dates stay date numbers.
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array', raw: true });
      const sheet = book.SheetNames.find((n) => n.trim().toLowerCase() === 'pr lines') || book.SheetNames[0];
      const table = XLSX.utils.sheet_to_json(book.Sheets[sheet], { header: 1, raw: true, defval: '' }) as unknown[][];
      await check(table);
    } catch (e: any) {
      setResult(null);
      setError(`Could not read ${file.name}: ${e?.message || e}`);
    }
  };

  const ready = (result?.rows || []).filter((r) => !r.errors.length);
  const failed = (result?.rows || []).filter((r) => r.errors.length);

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="pr-import-title">
      <DialogTitle id="pr-import-title">Import PR lines from Excel</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Fill the template (one row per line; the column names stay in the first row), then upload it, or copy the cells in Excel and paste them here.
            </Typography>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadPRLineTemplate(requisitionType)}>Download template</Button>
          </Stack>

          <Tabs value={tab} onChange={(_e, v) => { setTab(v); setResult(null); setError(''); }} aria-label="Import source">
            <Tab label="Upload Excel file" />
            <Tab label="Paste from Excel" />
          </Tabs>

          {tab === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={checking}>
                Choose file (.xlsx, .xls, .csv)
                <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
              </Button>
              {fileName ? <Typography variant="body2">{fileName}</Typography> : null}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <TextField
                label="Paste the cells here (Ctrl+V), with the column names in the first row"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                multiline
                minRows={5}
                maxRows={12}
                fullWidth
                inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <Box>
                <Button variant="contained" disabled={!pasted.trim() || checking} onClick={() => void check(textToTable(pasted))}>Check pasted lines</Button>
              </Box>
            </Stack>
          )}

          {checking ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Checking the lines…</Typography></Stack> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          {result && !checking ? (
            <Stack spacing={1}>
              <Alert severity={failed.length ? 'warning' : 'success'}>
                {ready.length} line{ready.length === 1 ? '' : 's'} ready
                {failed.length ? `; ${failed.length} row${failed.length === 1 ? '' : 's'} with errors will be skipped` : ''}.
              </Alert>
              {result.positional ? (
                <Alert severity="info">No column names found in the first row: the columns were read in the template's order.</Alert>
              ) : null}
              {result.unknownHeaders.length ? (
                <Alert severity="info">Ignored columns (not PR line columns): {result.unknownHeaders.join(', ')}</Alert>
              ) : null}
              {itemNote ? <Alert severity="info">{itemNote}</Alert> : null}
              {result.ambiguousDate ? (
                <Alert severity="info">
                  <Typography variant="body2">A date like {result.ambiguousDate} can be read two ways. How does your Excel show dates?</Typography>
                  <RadioGroup
                    row
                    value={result.dateOrder}
                    onChange={(e) => {
                      const order = e.target.value as DateOrder;
                      setDateOrder(order);
                      if (lastTable.current) void check(lastTable.current, order);
                    }}
                    aria-label="Date format"
                  >
                    {(['DMY', 'MDY'] as DateOrder[]).map((order) => (
                      <FormControlLabel
                        key={order}
                        value={order}
                        control={<Radio size="small" />}
                        label={`${order === 'DMY' ? 'Day/month/year' : 'Month/day/year'}: ${result.ambiguousDate} = ${displayDate(parseDate(result.ambiguousDate, { order }))}`}
                      />
                    ))}
                  </RadioGroup>
                </Alert>
              ) : null}
              <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader aria-label="Lines to import">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Req. Qty</TableCell>
                      <TableCell>UoM</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell>Delivery</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Check</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.slice(0, PREVIEW_ROWS).map((r) => (
                      <TableRow key={r.rowNumber} sx={r.errors.length ? { bgcolor: 'rgba(211, 47, 47, 0.06)' } : undefined}>
                        <TableCell>{r.rowNumber}</TableCell>
                        <TableCell>{String(r.line.itemcode ?? '')}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>{String(r.line.itemdescription ?? '')}</TableCell>
                        <TableCell align="right">{r.line.quantity ?? ''}</TableCell>
                        <TableCell>{String(r.line.uomcode ?? '')}</TableCell>
                        <TableCell align="right">{r.line.unitprice ?? ''}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{displayDate(r.line.deliverydate ?? '')}</TableCell>
                        <TableCell>{String(r.line.projectcode ?? '')}</TableCell>
                        <TableCell>
                          {r.errors.length
                            ? <Typography variant="caption" color="error">{r.errors.join('; ')}</Typography>
                            : <Chip size="small" color="success" label="OK" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {result.rows.length > PREVIEW_ROWS ? (
                <Typography variant="caption" color="text.secondary">Showing the first {PREVIEW_ROWS} of {result.rows.length} rows.</Typography>
              ) : null}
              {existingCount > 0 ? (
                <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as ImportMode)} aria-label="Where to put the lines">
                  <FormControlLabel value="append" control={<Radio />} label={`Add after the ${existingCount} existing line${existingCount === 1 ? '' : 's'}`} />
                  <FormControlLabel value="replace" control={<Radio />} label="Replace the existing lines" />
                </RadioGroup>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready.length || checking}
          onClick={() => onImport(ready.map((r) => r.line), existingCount > 0 ? mode : 'replace', items.current || [])}
        >
          Import {ready.length || ''} line{ready.length === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default PRLineImportDialog;
